import { PencilSquareIcon, PlusIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Chip, ConfirmDialog, DataTable, LinkButton, notify, PageHeader } from '../../components/ui';
import type { Column } from '../../components/ui';
import { useRemote } from '../../features/home/useRemote';
import { useApiError } from '../../hooks/useApiError';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import type { RootState } from '../../store';
import type { User } from '../../types/User';
import { Avatar } from './Avatar';
import { fullName } from './fullName';

const UserList = () => {
    const { t } = useTranslation('team');
    usePageTitle(t('list.pageTitle'));
    const apiError = useApiError();
    const me = useSelector((s: RootState) => s.auth.user);
    const isAdmin = me?.role?.name === 'Admin';

    const users = useRemote('users', () => api.get<User[]>('/users').then((r) => r.data));
    const [removed, setRemoved] = useState<string[]>([]);
    const [doomed, setDoomed] = useState<User | null>(null);
    const [busy, setBusy] = useState(false);

    const remove = useCallback(async () => {
        if (!doomed) return;
        setBusy(true);
        try {
            await api.delete(`/users/${doomed.id}`);
            setRemoved((ids) => [...ids, doomed.id]);
            notify.success(t('list.deleted', { name: fullName(doomed) }));
            setDoomed(null);
        } catch (e) {
            notify.error(apiError(e));
        } finally {
            setBusy(false);
        }
    }, [doomed, t, apiError]);

    const columns: Column<User>[] = [
        {
            id: 'name',
            header: t('list.name'),
            priority: 'primary',
            sortValue: (u) => fullName(u),
            cell: (u) => (
                <span className="flex items-center gap-3">
                    <Avatar first={u.firstName} last={u.lastName} fallback={u.email} />
                    <span className="truncate">
                        {fullName(u)}
                        {u.id === me?.id && <span className="text-muted"> {t('list.you')}</span>}
                        {u.isActive === false && <span className="text-muted"> {t('list.inactive')}</span>}
                    </span>
                </span>
            ),
        },
        { id: 'email', header: t('list.email'), priority: 'secondary', sortValue: (u) => u.email, cell: (u) => u.email },
        {
            id: 'role',
            header: t('list.role'),
            priority: 'meta',
            sortValue: (u) => u.role.name,
            cell: (u) => <Chip variant="neutral">{t(`roles.${u.role.name}`)}</Chip>,
        },
        ...(isAdmin
            ? [
                  {
                      id: 'restaurant',
                      header: t('list.restaurant'),
                      priority: 'meta' as const,
                      sortValue: (u: User) => u.restaurant?.name ?? '',
                      cell: (u: User) => u.restaurant?.name ?? '–',
                  },
              ]
            : []),
        {
            id: 'location',
            header: t('list.location'),
            priority: 'meta',
            sortValue: (u) => u.location?.name ?? '',
            cell: (u) => u.location?.name ?? (u.role.name === 'Staff' ? '–' : t('list.allLocations')),
        },
    ];

    const rows = (users.data ?? []).filter((u) => !removed.includes(u.id));

    return (
        <>
            <PageHeader
                title={t('list.title')}
                actions={
                    <LinkButton to="/users/create" variant="primary" icon={<PlusIcon className="size-5" aria-hidden="true" />}>
                        {t('list.add')}
                    </LinkButton>
                }
            />
            <DataTable
                rows={rows}
                columns={columns}
                getRowId={(u) => u.id}
                rowHref={(u) => `/users/edit/${u.id}`}
                rowLabel={fullName}
                rowActions={(u) => [
                    { id: 'edit', label: t('list.edit'), icon: PencilSquareIcon, to: `/users/edit/${u.id}` },
                    ...(u.id === me?.id
                        ? []
                        : [{ id: 'delete', label: t('list.delete'), icon: TrashIcon, tone: 'danger' as const, separated: true, onSelect: () => setDoomed(u) }]),
                ]}
                state={users.state}
                onRetry={users.reload}
                caption={t('list.caption')}
                empty={{
                    icon: UserGroupIcon,
                    title: t('list.emptyTitle'),
                    body: t('list.emptyBody'),
                    action: (
                        <LinkButton to="/users/create" variant="primary">
                            {t('list.add')}
                        </LinkButton>
                    ),
                }}
            />
            <ConfirmDialog
                open={doomed !== null}
                onClose={() => setDoomed(null)}
                onConfirm={() => void remove()}
                title={t('list.deleteTitle', { name: doomed ? fullName(doomed) : '' })}
                confirmLabel={t('list.delete')}
                busy={busy}
            >
                {t('list.deleteBody')}
            </ConfirmDialog>
        </>
    );
};

export default UserList;
